import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Layers3 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { t } = useLang();
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid(true); return; }
    api.get(`/auth/invite/${token}`).then((r) => { setInvite(r.data); setName(r.data.name || ""); }).catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password });
      applyAuth(data.token, data.user);
      toast.success(t("Conta ativada!", "Account activated!"));
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-card p-8">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-xl"><img src="/logo.png" alt="ReiMultiBlog" className="h-9 w-9 object-cover" /></span>
          <span className="font-heading text-lg font-extrabold">Rei<span className="text-primary">MultiBlog</span></span>
        </div>
        {invalid ? (
          <p className="text-sm text-destructive" data-testid="invite-invalid">{t("Convite inválido ou já utilizado.", "Invalid or already used invite.")}</p>
        ) : !invite ? (
          <p className="text-sm text-muted-foreground">{t("A verificar convite…", "Verifying invite…")}</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h1 className="font-heading text-2xl font-bold">{t("Aceitar convite", "Accept invite")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{invite.email}</p>
            </div>
            <div>
              <Label>{t("Nome", "Name")}</Label>
              <Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label>{t("Palavra-passe", "Password")}</Label>
              <Input data-testid="invite-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" />
            </div>
            <Button data-testid="invite-submit-btn" type="submit" disabled={loading} className="w-full">
              {loading ? t("A criar…", "Creating…") : t("Criar conta", "Create account")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
